import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

// ===================== models =====================

export interface SearchMemberHit {
  id: number;
  memberUniqueId?: string | null;
  memberSeriesNo?: string | null;
  fullName?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  panNumber?: string | null;
  aadhaarNumber?: string | null;
  loanNumber?: string | null;
  masterPolicyOnLoan?: string | null;
  masterPolicyName?: string | null;
  policyNo?: string | null;
  receiptNo?: string | null;
  paymentStatus?: string | null;
}

export interface SearchPaymentHit {
  id: number;
  paymentUniqueId?: string | null;
  gatewayOrderId?: string | null;
  gatewayPaymentId?: string | null;
  amount?: number | null;
  paymentStatus?: string | null;
  paymentMode?: string | null;
  paidAt?: string | null;
  createdDate?: string | null;
  memberId?: number | null;
  memberUniqueId?: string | null;
  memberName?: string | null;
  mobileNumber?: string | null;
  receiptNo?: string | null;
  gatewayName?: string | null;
}

export interface UniversalSearchResult {
  members: SearchMemberHit[];
  payments: SearchPaymentHit[];
}

// ===================== defensive mappers =====================
// Backend may serialize PascalCase (Dapper dynamic) or camelCase (typed DTO); read both.

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asNum(v: any): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapMember(x: any): SearchMemberHit {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    memberUniqueId: pick(x, 'MemberUniqueId', 'memberUniqueId') ?? null,
    memberSeriesNo: pick(x, 'MemberSeriesNo', 'memberSeriesNo') ?? null,
    fullName: pick(x, 'FullName', 'fullName') ?? null,
    mobileNumber: pick(x, 'MobileNumber', 'mobileNumber') ?? null,
    email: pick(x, 'Email', 'email') ?? null,
    panNumber: pick(x, 'PanNumber', 'panNumber') ?? null,
    aadhaarNumber: pick(x, 'AadhaarNumber', 'aadhaarNumber') ?? null,
    loanNumber: pick(x, 'LoanNumber', 'loanNumber') ?? null,
    masterPolicyOnLoan: pick(x, 'MasterPolicyONLOAN', 'masterPolicyONLOAN', 'masterPolicyOnLoan') ?? null,
    masterPolicyName: pick(x, 'MasterPolicyName', 'masterPolicyName') ?? null,
    policyNo: pick(x, 'PolicyNo', 'policyNo') ?? null,
    receiptNo: pick(x, 'ReceiptNo', 'receiptNo') ?? null,
    paymentStatus: pick(x, 'PaymentStatus', 'paymentStatus') ?? null,
  };
}

function mapPayment(x: any): SearchPaymentHit {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    paymentUniqueId: pick(x, 'PaymentUniqueId', 'paymentUniqueId') ?? null,
    gatewayOrderId: pick(x, 'GatewayOrderId', 'gatewayOrderId') ?? null,
    gatewayPaymentId: pick(x, 'GatewayPaymentId', 'gatewayPaymentId') ?? null,
    amount: pick(x, 'Amount', 'amount') ?? null,
    paymentStatus: pick(x, 'PaymentStatus', 'paymentStatus') ?? null,
    paymentMode: pick(x, 'PaymentMode', 'paymentMode') ?? null,
    paidAt: pick(x, 'PaidAt', 'paidAt') ?? null,
    createdDate: pick(x, 'CreatedDate', 'createdDate') ?? null,
    memberId: pick(x, 'MemberId', 'memberId') ?? null,
    memberUniqueId: pick(x, 'MemberUniqueId', 'memberUniqueId') ?? null,
    memberName: pick(x, 'MemberName', 'memberName') ?? null,
    mobileNumber: pick(x, 'MobileNumber', 'mobileNumber') ?? null,
    receiptNo: pick(x, 'ReceiptNo', 'receiptNo') ?? null,
    gatewayName: pick(x, 'GatewayName', 'gatewayName') ?? null,
  };
}

// ===================== service =====================

/**
 * Keyless universal search over members + payments (GET /api/Search/All). One term is
 * matched across every identifier field; the backend scopes to the caller's records.
 */
@Injectable({ providedIn: 'root' })
export class SearchService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  universal(q: string, limit = 8) {
    const params = new HttpParams().set('q', q).set('limit', limit);
    return this.http.get<ApiEnvelope<any>>(`${this.api}/Search/All`, { params }).pipe(
      map((res) => {
        const d = res?.data ?? {};
        const members = (pick<any[]>(d, 'members', 'Members') ?? []).map(mapMember);
        const payments = (pick<any[]>(d, 'payments', 'Payments') ?? []).map(mapPayment);
        return { ...res, data: { members, payments } as UniversalSearchResult };
      }),
    );
  }
}
